import { useEffect, useState, useCallback, useRef } from "react";
import { api, currency, fileUrl, uploadFile } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Bits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Pencil, Package, Upload, ImagePlus, FileImage, X } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", sku: "", price: 0, image_url: "", technical_drawing_url: "", description: "", bom: [] };

function ImageUploader({ label, hint, value, onChange, icon: Icon, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Dosya en fazla 8MB olabilir");
    setBusy(true);
    try {
      const url = await uploadFile(f);
      onChange(url);
      toast.success("Yüklendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yükleme hatası");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="border border-dashed border-slate-300 rounded-lg p-3 bg-slate-50/40">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">{label}</Label>
      </div>
      {value ? (
        <div className="relative group">
          <img src={fileUrl(value)} alt="" className="w-full h-40 object-cover rounded-md border border-slate-200" />
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 border border-slate-200 grid place-items-center hover:bg-red-50">
            <X className="h-4 w-4 text-red-600" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={testId}
          className="w-full h-40 rounded-md border-2 border-dashed border-slate-300 bg-white hover:bg-slate-50 hover:border-blue-400 transition-colors grid place-items-center text-slate-500"
        >
          <div className="flex flex-col items-center gap-1">
            <Upload className="h-6 w-6" />
            <span className="text-xs font-semibold">{busy ? "Yükleniyor..." : "Dosya Seç veya Sürükle"}</span>
            {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
          </div>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
    </div>
  );
}

export default function Products() {
  const [list, setList] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => api.get("/products").then((r) => setList(r.data)), []);
  useEffect(() => { load(); api.get("/materials").then((r) => setMaterials(r.data)); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...empty, ...p, bom: p.bom || [] });
    setEditId(p.id); setOpen(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("İsim zorunlu");
    const payload = {
      ...form,
      price: Number(form.price || 0),
      bom: form.bom.filter((b) => b.material_id).map((b) => ({ material_id: b.material_id, qty: Number(b.qty) })),
    };
    if (editId) await api.put(`/products/${editId}`, payload);
    else await api.post("/products", payload);
    toast.success("Kaydedildi"); setOpen(false); load();
  };
  const del = async (p) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/products/${p.id}`); load(); };

  const addBom = () => setForm({ ...form, bom: [...form.bom, { material_id: "", qty: 1 }] });
  const setBom = (i, k, v) => setForm({ ...form, bom: form.bom.map((b, idx) => idx === i ? { ...b, [k]: v } : b) });
  const rmBom = (i) => setForm({ ...form, bom: form.bom.filter((_, idx) => idx !== i) });

  return (
    <div className="p-8">
      <PageHeader eyebrow="Katalog" title="Ürünler ve Reçeteler" subtitle="Ürünlerinizi, görselleri, teknik çizimleri ve BOM reçetelerini yönetin"
        actions={<Button data-testid="product-new-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" />Yeni Ürün</Button>} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editId ? "Ürün Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ürün Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
            </div>
            <div>
              <Label>Fiyat ($)</Label>
              <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="max-w-xs" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ImageUploader
                label="Bitmiş Ürün Görseli"
                hint="JPG / PNG · maks. 8MB"
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                icon={ImagePlus}
                testId="product-image-upload"
              />
              <ImageUploader
                label="Teknik Çizim"
                hint="Üretim referansı · JPG / PNG"
                value={form.technical_drawing_url}
                onChange={(url) => setForm({ ...form, technical_drawing_url: url })}
                icon={FileImage}
                testId="product-drawing-upload"
              />
            </div>
            <div><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label>Reçete (BOM) — Hammadde tüketimi</Label>
                <Button size="sm" variant="outline" onClick={addBom}><Plus className="h-3 w-3 mr-1" />Malzeme ekle</Button>
              </div>
              {form.bom.length === 0 && <div className="text-xs text-slate-500">Bu ürün için henüz reçete tanımlı değil.</div>}
              {form.bom.map((b, i) => (
                <div key={b.material_id || `new-${i}`} className="flex gap-2 items-center mb-2">
                  <Select value={b.material_id} onValueChange={(v) => setBom(i, "material_id", v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Malzeme seçin" /></SelectTrigger>
                    <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit})</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" value={b.qty} onChange={(e) => setBom(i, "qty", e.target.value)} className="w-28" placeholder="Miktar" />
                  <Button size="icon" variant="ghost" onClick={() => rmBom(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button data-testid="product-save-btn" onClick={submit} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{preview?.title}</DialogTitle></DialogHeader>
          {preview?.url && <img src={fileUrl(preview.url)} alt="" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>

      {list.length === 0 ? (
        <EmptyState icon={Package} title="Henüz ürün yok" description="Ürün kataloğunuzu oluşturun." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <Card key={p.id} className="border-slate-200 overflow-hidden">
              <div className="grid grid-cols-2">
                {p.image_url ? (
                  <button type="button" onClick={() => setPreview({ url: p.image_url, title: `${p.name} — Bitmiş Ürün` })} className="h-32 relative group">
                    <img src={fileUrl(p.image_url)} alt={p.name} className="h-32 w-full object-cover" />
                    <span className="absolute bottom-1 left-1 text-[9px] font-bold uppercase tracking-widest bg-black/60 text-white px-1.5 py-0.5 rounded">Bitmiş</span>
                  </button>
                ) : (
                  <div className="h-32 bg-slate-100 grid place-items-center border-r border-slate-200"><ImagePlus className="h-6 w-6 text-slate-400" /></div>
                )}
                {p.technical_drawing_url ? (
                  <button type="button" onClick={() => setPreview({ url: p.technical_drawing_url, title: `${p.name} — Teknik Çizim` })} className="h-32 relative group">
                    <img src={fileUrl(p.technical_drawing_url)} alt="" className="h-32 w-full object-cover bg-white" />
                    <span className="absolute bottom-1 left-1 text-[9px] font-bold uppercase tracking-widest bg-blue-600 text-white px-1.5 py-0.5 rounded">Teknik</span>
                  </button>
                ) : (
                  <div className="h-32 bg-slate-50 grid place-items-center"><FileImage className="h-6 w-6 text-slate-300" /></div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-display font-bold text-slate-900">{p.name}</h3>
                  <div className="font-bold text-blue-600">{currency(p.price)}</div>
                </div>
                <div className="text-xs text-slate-500 font-mono mb-2">{p.sku || "-"}</div>
                <div className="text-xs text-slate-500 line-clamp-2 mb-3">{p.description || "Açıklama yok"}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Reçete</div>
                <div className="text-xs text-slate-600 mb-3">
                  {p.bom?.length ? p.bom.map((b) => {
                    const m = materials.find((x) => x.id === b.material_id);
                    return m ? `${b.qty} ${m.unit} ${m.name}` : null;
                  }).filter(Boolean).join(" · ") : <span className="text-slate-400">Tanımsız</span>}
                </div>
                <div className="flex justify-end gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(p)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
